import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { fetchSportsbookAnalyticsSummary } from "./fetchSportsbookAnalyticsSummary";
import { mapAnalyticsSummaryItemsToRows } from "./sportsbookAnalyticsSummaryMapper";
import { filterToken } from "./sportsbookQueryUtils";
import type {
  SportsbookAnalyticsSummaryQueryParams,
  SportsbookAnalyticsSummaryResult,
  SportsbookSummaryFilterMetaDto,
} from "./sportsbookAnalyticsSummaryTypes";

export const SPORTSBOOK_ANALYTICS_SUMMARY_QUERY_KEY =
  "SPORTSBOOK_ANALYTICS_SUMMARY" as const;

const EMPTY_FILTERS: SportsbookSummaryFilterMetaDto[] = [];

/**
 * Loads the reporting summary table for one date range, one page, and the
 * currently applied filter set. Backend supports direct page access, so each
 * render is a single GET keyed on the full filter surface.
 */
export function useSportsbookAnalyticsSummary(
  params: SportsbookAnalyticsSummaryQueryParams,
) {
  const {
    start_date,
    end_date,
    page,
    size,
    money_type,
    bet_status,
    bet_type,
    sport,
    league,
    odds_provider,
    state,
  } = params;

  const query = useQuery<SportsbookAnalyticsSummaryResult>({
    queryKey: [
      SPORTSBOOK_ANALYTICS_SUMMARY_QUERY_KEY,
      start_date,
      end_date,
      page,
      size,
      filterToken(money_type),
      filterToken(bet_status),
      filterToken(bet_type),
      filterToken(sport),
      filterToken(league),
      filterToken(odds_provider),
      filterToken(state),
    ],
    queryFn: () =>
      fetchSportsbookAnalyticsSummary({
        start_date,
        end_date,
        page,
        size,
        money_type,
        bet_status,
        bet_type,
        sport,
        league,
        odds_provider,
        state,
      }),
    enabled: Boolean(start_date && end_date),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

  const rows = useMemo(() => {
    if (!query.data?.items?.length) return [];
    return mapAnalyticsSummaryItemsToRows(query.data.items);
  }, [query.data?.items]);

  return {
    ...query,
    rows,
    totalRowCount: query.data?.totalCount ?? 0,
    page: query.data?.page ?? page,
    size: query.data?.size ?? size,
    pages: query.data?.pages ?? 0,
    filters: query.data?.filters ?? EMPTY_FILTERS,
    wasRangeClamped: query.data?.wasRangeClamped ?? false,
  };
}
